import type { AiPracticeArea } from '@portfolio/shared';
import type { JSX } from 'react';
import { Link } from 'react-router';

import { Card } from '@/components/ui/Card';
import { ArrowRightIcon, ExternalLinkIcon } from '@/components/ui/icons';

interface AiPracticeGridProps {
  areas: readonly AiPracticeArea[];
  headingLevel?: 'h3' | 'h4';
}

/**
 * Each card names where the work was done and links to it, so the summary can
 * always be checked against the underlying case study or live project.
 */
export function AiPracticeGrid({
  areas,
  headingLevel: Heading = 'h3',
}: AiPracticeGridProps): JSX.Element {
  return (
    <ul className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
      {areas.map((area) => (
        <Card as="li" key={area.id} className="flex min-w-0 flex-col">
          <Heading className="text-base">{area.title}</Heading>
          <p className="mt-3 text-sm text-muted">{area.description}</p>

          <ul className="mt-5 flex flex-col gap-2 border-t border-line pt-4 text-sm text-subtle">
            {area.points.map((point) => (
              <li key={point} className="flex gap-2">
                <span
                  aria-hidden="true"
                  className="mt-2 size-1.5 shrink-0 rounded-full bg-accent"
                />
                {point}
              </li>
            ))}
          </ul>

          <p className="mt-5 flex-1" />

          {area.evidenceExternal ? (
            <a
              href={area.evidenceHref}
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex min-h-11 items-center gap-2 wrap-anywhere text-sm font-medium text-accent underline underline-offset-2 hover:text-accent-strong"
            >
              {area.evidenceLabel}
              <ExternalLinkIcon width={14} height={14} />
              <span className="sr-only">— evidence for {area.title} (opens in a new tab)</span>
            </a>
          ) : (
            <Link
              to={area.evidenceHref}
              className="inline-flex min-h-11 items-center gap-2 text-sm font-medium text-accent underline underline-offset-2 hover:text-accent-strong"
            >
              {area.evidenceLabel}
              <span className="sr-only">— evidence for {area.title}</span>
              <ArrowRightIcon width={14} height={14} />
            </Link>
          )}
        </Card>
      ))}
    </ul>
  );
}
